import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Images,
  FolderTree,
  Cpu,
  LogOut,
  ArrowLeft,
  Users,
  Inbox,
  ShieldAlert,
  Sparkles,
  ImagePlus,
} from 'lucide-react';
import { Container } from '@/components/layout/container';
import { Logo } from '@/components/common/logo';
import { Button } from '@/components/ui/button';
import { SkipLink } from '@/components/common/skip-link';
import { SeoHead } from '@/components/common/seo-head';
import { useAuth, useAuthBootstrap } from '@/stores/auth';
import { signOutAndRedirect } from '@/lib/auth-client';
import { resolvePublicAppUrl } from '@/lib/seo';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { useEffect } from 'react';

const navItems = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/prompts', label: 'Prompts', icon: Images },
  { to: '/admin/submissions', label: 'Submissions', icon: Inbox },
  { to: '/admin/safety', label: 'Safety', icon: ShieldAlert },
  { to: '/admin/categories', label: 'Categories', icon: FolderTree },
  { to: '/admin/models', label: 'Models', icon: Cpu },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/showcase', label: 'Showcase', icon: Sparkles },
  { to: '/admin/hero-prompts', label: 'Hero Images', icon: ImagePlus },
];

export const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  useAuthBootstrap();
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/signin', { replace: true });
    } else if (!isLoading && user && user.role !== 'admin') {
      navigate('/', { replace: true });
      toast.error('Admin access required');
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  if (isLoading || !isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-text-muted">Loading admin...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead
        publicAppUrl={resolvePublicAppUrl()}
        title="Admin"
        description="Visualry admin console — manage prompts, categories, models, and users."
        noindex
      />
      <SkipLink />
      <header className="sticky top-0 z-30 border-b border-border-subtle bg-background/88 backdrop-blur-xl">
        <Container size="xl" className="flex h-14 items-center gap-4">
          <Logo size="sm" />
          <span className="text-[10px] uppercase tracking-widest text-accent border border-accent/40 bg-accent-soft rounded-full px-2 py-0.5">
            Admin
          </span>
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            <Link to="/">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to site
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { void signOutAndRedirect(() => { window.location.href = '/'; }); }}
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </Button>
        </Container>
      </header>
      <div className="flex-1 flex">
        <aside className="hidden md:block w-56 border-r border-border-subtle bg-surface-soft/40 backdrop-blur">
          <nav className="p-3 space-y-0.5">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive || location.pathname === item.to
                      ? 'bg-accent-soft text-foreground ring-1 ring-accent/20'
                      : 'text-text-secondary hover:text-foreground hover:bg-surface-hover',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main id="main" tabIndex={-1} className="flex-1 min-w-0">
          <Container size="xl" className="py-6">
            {children}
          </Container>
        </main>
      </div>
    </div>
  );
};
